import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import PageTitle from 'Components/PageTitle'
import { ZoneInfoList } from 'Services/SalesManagement/Features/ZoneInfo/Components/ZoneInfoList'
import {
  fetchZoneInfoList,
  type ZoneInfoParamShape
} from 'Services/SalesManagement/Features/ZoneInfo/zoneInfoSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import useQuery from 'Util/getQueryParamsHook'
import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { type default as React, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export default function ZoneInfoListPage() {
  const title = t`Zone infos`
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/zone-infos`
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const queryParams = useQuery()

  const { zoneInfos, count, loading } = useTypedSelector(state => state.zoneInfo)

  const [params, setParams] = useState<ZoneInfoParamShape>({
    ordering: queryParams.get('ordering') ?? '-created',
    page: queryParams.get('page') ? parseInt(queryParams.get('page') as string) : 1
  })

  useEffect(() => {
    dispatch(fetchZoneInfoList(params)).then(() => {
      setQueryParams()
    })
  }, [dispatch, params])

  const setQueryParams = () => {
    const query: string[] = []
    Object.keys(params).forEach((key: string) => {
      const value = params[key as keyof ZoneInfoParamShape]
      if (value) {
        query.push(key + '=' + value)
      }
    })
    const queryString = query.join('&')

    navigate({
      pathname: location.pathname,
      search: '?' + queryString
    })
  }

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = params.ordering.includes('-')
    setParams(prevState => ({ ...prevState, ordering: `${isAsc ? '' : '-'}${property}` }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({ ...prevState, page: newPage + 1 }))
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: title
    }
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <BackButton link={SALES_MANAGEMENT_ROOT_URL} />
        <PageTitle>{title}</PageTitle>
        <Typography component="p" variant="subtitle1" sx={{ fontSize: '15px', mb: 4 }}>
          {t`In the zone info section you can manage the zone info content which is displayed in the Waltti webshop.`}
        </Typography>
      </Box>
      <CustomButton
        sx={{ mt: 1, mb: 2 }}
        onClick={() => navigate(`${baseUrl}/create/`)}
        size="small"
        startIcon={<AddIcon />}
      >
        {t`Create new zone info`}
      </CustomButton>

      <ZoneInfoList
        rows={zoneInfos}
        loading={loading}
        params={params}
        handleRequestSort={handleRequestSort}
        handleChangePage={handleChangePage}
        count={count}
      />
    </Box>
  )
}
